import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from './Icon';
import { CountUp } from './CountUp';
import { CoinAvatar } from './CoinAvatar';
import { useT } from '../i18n';
import { POPULAR_PAIRS } from '../lib/content';

function statIcon(label: string): string {
  const l = label.toLowerCase();
  if (l.includes('coin') || l.includes('عمل') || l.includes('币') || l.includes('монет')) return 'layers';
  if (l.includes('block') || l.includes('сет') || l.includes('网络') || l.includes('بلوك') || l.includes('区块链')) return 'globe';
  if (l.includes('kyc') || l.includes('идентиф') || l.includes('身份') || l.includes('هوية')) return 'shield';
  return 'bolt';
}

/* ---------- Trust bar ---------- */
export function TrustBar() {
  const t = useT();
  return (
    <div className="trust-bar">
      {t.trustPoints.map((item) => (
        <div className="trust-item" key={item.title}>
          <Icon name={item.icon as never} width={18} height={18} />
          <span>{item.title}</span>
        </div>
      ))}
    </div>
  );
}

/* ---------- Stats band ---------- */
export function StatsBand() {
  const t = useT();
  return (
    <div className="stats-band">
      {t.stats.map((s) => (
        <div className="cell" key={s.label}>
          <div className="cell-icon"><Icon name={statIcon(s.label) as never} /></div>
          <div className="num">
            <CountUp end={Number(s.num) || 0} />
            <span className="unit">{s.unit}</span>
          </div>
          <div className="label">{s.label}</div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Popular pairs grid ---------- */
export function PopularPairs({ limit }: { limit?: number }) {
  const pairs = limit ? POPULAR_PAIRS.slice(0, limit) : POPULAR_PAIRS;
  return (
    <div className="pairs-grid">
      {pairs.map((p) => (
        <Link key={p.label} to={`/exchange/${p.from}-to-${p.to}`} className="pair-card">
          <span className="pair-symbols">
            <span className="pair-icons">
              <CoinAvatar ticker={p.from} size={26} />
              <CoinAvatar ticker={p.to} size={26} />
            </span>
            <span className="pair-tickers">
              {p.from.toUpperCase()}
              <span className="arrow"> → </span>
              {p.to.split('_')[0].toUpperCase()}
            </span>
          </span>
          <span className="pair-cta">
            Swap <Icon name="arrow-up-right" width={14} height={14} />
          </span>
        </Link>
      ))}
    </div>
  );
}

/* ---------- Steps ---------- */
export function HowItWorksSteps() {
  const t = useT();
  return (
    <div className="steps">
      {t.steps.map((s, i) => (
        <div className="step" key={i}>
          <div className="step-num">{i + 1}</div>
          <h3>{s.title}</h3>
          <p>{s.text}</p>
        </div>
      ))}
    </div>
  );
}

/* ---------- FAQ accordion ---------- */
export function FaqList({ items, limit }: { items?: { q: string; a: string }[]; limit?: number }) {
  const t = useT();
  const list = items || t.faqs;
  const shown = limit ? list.slice(0, limit) : list;
  const [open, setOpen] = useState<number | null>(0);
  return (
    <div className="faq-list">
      {shown.map((f, i) => {
        const isOpen = open === i;
        return (
          <div className={`faq-item ${isOpen ? 'open' : ''}`} key={f.q}>
            <button className="faq-q" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : i)}>
              <span>{f.q}</span>
              <Icon name="chevron-down" className="chev" width={20} height={20} />
            </button>
            <div className="faq-a">
              <div className="faq-a-inner">{f.a}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- CTA section ---------- */
export function CtaSection() {
  const t = useT();
  return (
    <section className="cta-section">
      <div className="container">
        <div className="cta-card">
          <span className="eyebrow">{t.cta.eyebrow}</span>
          <h2>{t.cta.titleA}<br />{t.cta.titleB}</h2>
          <p>{t.cta.desc}</p>
          <div className="hero-cta">
            <Link to="/" className="btn btn-primary btn-large">
              {t.cta.startSwapping}
              <Icon name="arrow-right" width={18} height={18} />
            </Link>
            <Link to="/coins" className="btn btn-outline btn-large">{t.cta.browseCoins}</Link>
          </div>
        </div>
      </div>
    </section>
  );
}
